import { RepositoryBuildInfo } from '@back4app2/sdk/lib/Back4app2';
import { API_V2_URL } from '../settings';

/**
 * The API v2 (/v1) from the browser: the Containers code upload and creating
 * an app from it. Plain fetch with the session cookie, like the other REST
 * calls here (useReleases, useCloudTree) — the SDK only speaks GraphQL.
 *
 * Every error the API returns has the same shape, and the `nextAction` in it
 * is written to be shown as is.
 */

export class ApiV2Error extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly nextAction?: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    Object.setPrototypeOf(this, ApiV2Error.prototype);
  }
}

/**
 * What the dashboard says for an API error. The API's `nextAction` is written
 * for whoever reads the response — and for some codes that is a CLI or an
 * agent ("finalize it with POST /v1/uploads/{id}/finalize"). A person at the
 * dashboard did none of that by hand, so those get the dashboard's own
 * sentence; the rest show the API's nextAction, or its message.
 *
 * Only client errors are shown as they are: a 5xx or a network failure says
 * nothing the person can act on, so `fallback` names what did not happen.
 */
const DASHBOARD_ACTIONS: Record<string, string> = {
  UPLOAD_INCOMPLETE: 'Some files did not arrive. Send the folder again.',
  UPLOAD_EXPIRED: 'This upload expired. Send the folder again; files already sent are not sent again.',
  UPLOAD_NOT_FOUND: 'This upload is no longer known. Send the folder again.',
  UPLOAD_IN_USE: "This code is another app's current or previous code. Send the folder again for this app.",
  MANIFEST_MISMATCH: 'This upload changed since it was sent. Send the folder again.',
  UPLOAD_FAILED: 'A file could not be sent. Check your connection and try again.',
};

export const userFacingError = (e: unknown, fallback: string): string => {
  if (!(e instanceof ApiV2Error) || e.status >= 500) {
    return fallback;
  }
  // Status 0 is "never answered": a network failure, or a storage PUT that
  // did not go through — the latter has words of its own.
  if (e.status === 0 && e.code !== 'UPLOAD_FAILED') {
    return fallback;
  }
  return DASHBOARD_ACTIONS[e.code] || e.nextAction || e.message;
};

/** The per-file problems of an INVALID_MANIFEST error, as "path: problem". */
export const errorProblems = (e: unknown): string[] => {
  const problems = e instanceof ApiV2Error ? e.details?.problems : undefined;
  if (!Array.isArray(problems)) {
    return [];
  }
  return problems.map((item: { path?: string; index?: number; problem: string }) =>
    item.path ? `${item.path}: ${item.problem}` : item.problem
  );
};

const request = async <T>(
  method: string,
  path: string,
  body?: unknown,
  headers: Record<string, string> = {}
): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${API_V2_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'x-back4app-client': 'dashboard',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiV2Error(0, 'NETWORK_ERROR', 'Could not reach Back4App. Check your connection and try again.');
  }

  const payload = await response.json().catch(() => undefined);

  if (!response.ok) {
    const error = payload?.error || {};
    throw new ApiV2Error(
      response.status,
      error.code || 'INTERNAL_ERROR',
      error.message || 'Something went wrong on our side.',
      error.nextAction,
      error.details
    );
  }

  return payload as T;
};

export interface UploadFileEntry {
  path: string;
  sha256: string;
  size: number;
  mode: number;
}

export interface UploadMissingFile {
  sha256: string;
  size: number;
  upload: { url: string; headers: Record<string, string> };
}

export interface UploadView {
  uploadId: string;
  manifestSha256: string;
  status: 'PENDING' | 'READY' | 'EXPIRED';
  fileCount: number;
  totalBytes: number;
  newBytes: number;
  missing: UploadMissingFile[];
  expiresAt: string;
}

/** `appId` for new code of an existing app: the upload is that app's. */
export const createUpload = (files: UploadFileEntry[], appId?: string) =>
  request<UploadView>('POST', '/v1/uploads', appId ? { appId, files } : { files });

export const getUpload = (uploadId: string) =>
  request<UploadView>('GET', `/v1/uploads/${uploadId}`);

export const finalizeUpload = (uploadId: string) =>
  request<UploadView>('POST', `/v1/uploads/${uploadId}/finalize`);

export const uploadBuildInfo = (uploadId: string, rootDir: string) =>
  request<RepositoryBuildInfo>(
    'GET',
    `/v1/uploads/${uploadId}/build-info?rootDir=${encodeURIComponent(rootDir)}`
  );

export interface CreateAppFromUploadInput {
  name: string;
  uploadId: string;
  manifestSha256: string;
  rootDirPath?: string;
  envVars?: { key: string; value?: string }[];
  healthCheckEndpoint?: string;
  exposedPort?: number;
  startCommand?: string;
  builderPreference?: string;
  isStaticSite?: boolean;
  runtimeVersion?: string;
  planId?: string;
  featureIds?: string[];
  databaseUrlEnvKey?: string;
}

/** Waits for the app to exist (a few seconds) and returns its id. */
export const createAppFromUpload = async (input: CreateAppFromUploadInput): Promise<string> => {
  // One key per click on Create: if the call is sent again (the browser's
  // own retry of a dropped connection), the API answers with the app the
  // first one made instead of making another.
  const result = await request<{ appId?: string; taskId?: string }>('POST', '/v1/containers/apps', input, {
    'Idempotency-Key': crypto.randomUUID(),
  });

  if (!result.appId) {
    // 202: still being created. The apps list picks it up in a moment.
    throw new ApiV2Error(
      202,
      'CREATION_PENDING',
      'Your app is still being created. It will show up in your apps in a moment.'
    );
  }

  return result.appId;
};

export interface AppSourceUpload {
  uploadId: string;
  manifestSha256: string;
  fileCount: number;
  totalBytes: number;
  uploadedAt: string;
  pinnedAs: 'CURRENT' | 'PREVIOUS' | null;
}

export interface AppSource {
  appId: string;
  /** What the next deploy builds. */
  latest: AppSourceUpload | null;
  /** The two kept for rollback. */
  current: AppSourceUpload | null;
  previous: AppSourceUpload | null;
}

export const getAppSource = (appId: string) =>
  request<AppSource>('GET', `/v1/containers/apps/${appId}/source`);

/** New code for an upload app: becomes its latest code and deploys. */
export const deployUpload = (appId: string, uploadId: string, manifestSha256: string) =>
  request<{ deploymentId: string }>('POST', `/v1/containers/apps/${appId}/deployments`, {
    uploadId,
    manifestSha256,
  });

export interface SourceEditsResult {
  uploadId: string;
  manifestSha256: string;
  fileCount: number;
  totalBytes: number;
  changed: number;
  created: number;
  deleted: number;
  renamed: number;
}

/**
 * The editor's changes as a new upload made from the latest code, ready to
 * deploy with `deployUpload`. 409 SOURCE_CHANGED (details.latest names the
 * code now) when `baseUploadId` is no longer the app's latest.
 */
export const editSource = (
  appId: string,
  body: {
    baseUploadId: string;
    changed: { path: string; content: string }[];
    deleted: string[];
    renamed: { from: string; to: string }[];
  }
) => request<SourceEditsResult>('POST', `/v1/containers/apps/${appId}/source/edits`, body);

// The code of one deployment, while it is kept; without one, the latest.
const deploymentQuery = (deploymentId?: string) =>
  deploymentId ? `deployment=${encodeURIComponent(deploymentId)}` : '';

export type SourceFolderEntry =
  | { type: 'FOLDER'; name: string; path: string; fileCount: number }
  | { type: 'FILE'; name: string; path: string; size: number };

export interface SourceFolder {
  dir: string;
  /** Files in the whole code, not in this folder. */
  fileCount: number;
  /** Entries in this folder; `entries` stops at the API's cap when `truncated`. */
  total: number;
  truncated: boolean;
  entries: SourceFolderEntry[];
}

/** One folder of the code ('' for the top), asked for when it is opened. */
export const listSourceFolder = (appId: string, dir: string, deploymentId?: string) =>
  request<SourceFolder>(
    'GET',
    `/v1/containers/apps/${appId}/source/tree?dir=${encodeURIComponent(dir)}${
      deploymentId ? `&${deploymentQuery(deploymentId)}` : ''
    }`
  );

export interface SourceSearch {
  query: string;
  /** How many paths matched; `files` holds the first ones. */
  total: number;
  truncated: boolean;
  files: { path: string; size: number }[];
}

/** Files whose path contains `query`, ignoring case. */
export const searchSourceFiles = (appId: string, query: string, deploymentId?: string) =>
  request<SourceSearch>(
    'GET',
    `/v1/containers/apps/${appId}/source/search?q=${encodeURIComponent(query)}${
      deploymentId ? `&${deploymentQuery(deploymentId)}` : ''
    }`
  );

export type SourceFileContent =
  | { path: string; size: number; kind: 'TEXT'; content: string }
  | { path: string; size: number; kind: 'BINARY' | 'TOO_LARGE' };

/** One file of the app's latest code, as text (binary and large files come back as a notice). */
export const sourceFileContent = (appId: string, path: string, deploymentId?: string) =>
  request<SourceFileContent>(
    'GET',
    `/v1/containers/apps/${appId}/source/files/content?path=${encodeURIComponent(path)}${
      deploymentId ? `&${deploymentQuery(deploymentId)}` : ''
    }`
  );

/**
 * READY once the .zip is built, with where the API streams it; PREPARING while
 * it builds. The API answers a path of its own, never a storage link: the
 * download goes through it, with the session, so access is checked then too.
 */
export type ArchiveResult = { status: 'READY'; url: string } | { status: 'PREPARING' };

const withApiUrl = (result: ArchiveResult): ArchiveResult =>
  result.status === 'READY' && result.url.startsWith('/')
    ? { ...result, url: `${API_V2_URL}${result.url}` }
    : result;

export const archiveSource = (appId: string) =>
  request<ArchiveResult>('POST', `/v1/containers/apps/${appId}/source/archive`).then(withApiUrl);

/**
 * Per row of the Deployments list. Rollback: only the previous deployment is
 * AVAILABLE; NOT_KEPT for older ones, NEVER_LIVE for failed or canceled ones.
 * Download: ZIP while the upload is kept, EXPIRED after, GITHUB for a commit.
 */
export interface DeploymentActions {
  /** SAME_CODE: a redeploy of the live code — no Roll back on its row. */
  rollback: 'CURRENT' | 'SAME_CODE' | 'AVAILABLE' | 'IN_PROGRESS' | 'NOT_KEPT' | 'NEVER_LIVE';
  download: 'ZIP' | 'GITHUB' | 'EXPIRED' | 'NONE';
  githubUrl?: string;
  /** 1 for the app's first deployment, in the order they were made. */
  number: number;
  /** When it was made (ISO) and where it got to (a DeploymentStatus name). */
  createdAt: string;
  status: string;
  source: 'UPLOAD' | 'GITHUB' | 'IMAGE';
  /** Upload hash, commit SHA or image digest, short. */
  codeHash?: string;
  /** CODE: the Source Code screen; GITHUB: viewUrl; EXPIRED: no longer kept; NONE: no code. */
  view: 'CODE' | 'GITHUB' | 'EXPIRED' | 'NONE';
  viewUrl?: string;
}

export const getDeploymentActions = (appId: string) =>
  request<Record<string, DeploymentActions>>('GET', `/v1/containers/apps/${appId}/deployments/actions`);

export const archiveDeployment = (appId: string, deploymentId: string) =>
  request<ArchiveResult>('POST', `/v1/containers/apps/${appId}/deployments/${deploymentId}/archive`).then(
    withApiUrl
  );

export interface RollbackDeployment {
  deploymentId: string;
  /** "Upload c59f168", "abc1234 · Fix login" or an image reference. */
  label: string;
  deployedAt: string;
  isRollback: boolean;
}

export type RollbackInfo =
  | {
      available: true;
      /** IMAGE: the kept image runs again, in seconds. REBUILD: it is gone, the previous code is built. */
      mode: 'IMAGE' | 'REBUILD';
      current: RollbackDeployment;
      previous: RollbackDeployment;
    }
  | {
      available: false;
      /** SAME_CODE: the previous deployment is a redeploy of the live code. */
      reason: 'NO_PREVIOUS' | 'IMAGE_GONE' | 'IN_PROGRESS' | 'SAME_CODE';
      current?: RollbackDeployment;
      previous?: RollbackDeployment;
    };

/** What "Roll back to previous" would do: the live deployment and the previous one. */
export const getRollback = (appId: string) =>
  request<RollbackInfo>('GET', `/v1/containers/apps/${appId}/rollback`);

/** Runs the previous deployment's image again; the deployment runs on its own. */
export const rollbackApp = (appId: string, deploymentId?: string) =>
  request<{ deploymentId: string; mode: 'IMAGE' | 'REBUILD'; to: RollbackDeployment }>(
    'POST',
    `/v1/containers/apps/${appId}/rollback`,
    deploymentId ? { deploymentId } : undefined
  );

/**
 * A login an AI agent started (GET /oauth/authorize sent the browser here):
 * what the authorization screen shows, and its two buttons. Under /oauth, not
 * /v1, with the login's other routes.
 */
export interface LoginRequest {
  id: string;
  /** CODE: an AI agent's login. DEVICE: the CLI's, started in a terminal. */
  kind: 'CODE' | 'DEVICE';
  status: 'PENDING' | 'WAITING_EMAIL';
  /** The name the client registered with. */
  clientName: string;
  /** The host (or app scheme) the browser goes back to. Empty for DEVICE. */
  returnTo: string;
  /** DEVICE: the code to compare with the one in the terminal. */
  userCode?: string;
  expiresAt: string;
  signedInAs: string;
  emailVerified: boolean;
}

/** Authorized, or waiting for the account's e-mail to be confirmed. */
export type LoginApproval =
  // No way back for a DEVICE login: the terminal finds out by itself.
  | { status: 'APPROVED'; redirectTo: string | null }
  | { status: 'WAITING_EMAIL'; expiresAt: string };

export const getLoginRequest = (requestId: string) =>
  request<LoginRequest>('GET', `/oauth/requests/${encodeURIComponent(requestId)}`);

/** The login a terminal's short code belongs to. */
export const getDeviceLoginRequest = (userCode: string) =>
  request<LoginRequest>('GET', `/oauth/device?user_code=${encodeURIComponent(userCode)}`);

export const approveLoginRequest = (requestId: string) =>
  request<LoginApproval>('POST', `/oauth/requests/${encodeURIComponent(requestId)}/approve`);

export const denyLoginRequest = (requestId: string) =>
  request<{ redirectTo: string | null }>(
    'POST',
    `/oauth/requests/${encodeURIComponent(requestId)}/deny`
  );

/**
 * The claim of a temporary account: an AI agent deployed before the person
 * had an account, and the claim link moves what it created to theirs. The
 * token (the link's fragment) goes in the body, never in an address.
 */
export interface ClaimApp {
  id: string;
  name: string;
  url: string | null;
  lastDeploymentStatus: string | null;
}

export interface ClaimPreview {
  /** When the temporary account, and the apps with it, are deleted. */
  expiresAt: string;
  /** The agent that asked for the account, when it said who it is. */
  createdBy: string | null;
  signedInAs: string;
  apps: ClaimApp[];
}

export const previewClaim = (token: string) =>
  request<ClaimPreview>('POST', '/v1/claims/preview', { token });

interface ClaimProgress {
  status?: 'DONE' | 'IN_PROGRESS';
  apps?: { id: string; name: string }[];
}

const CLAIM_POLL_MS = 2000;
const CLAIM_GIVE_UP_MS = 15 * 60 * 1000;

/**
 * Moves the apps of a claim link to the signed-in account. Copying the code
 * of a project with many files takes longer than one request may: the API
 * answers IN_PROGRESS and goes on, and the same call is sent again until it
 * says it is done. `apps` is empty when the API finished in a request it
 * does not remember; the preview has the same apps.
 */
export const claimApps = async (token: string) => {
  const startedAt = Date.now();

  for (;;) {
    const progress = await request<ClaimProgress>('POST', '/v1/claims', { token });

    if (progress.status !== 'IN_PROGRESS') {
      return { apps: progress.apps || [] };
    }
    if (Date.now() - startedAt > CLAIM_GIVE_UP_MS) {
      throw new ApiV2Error(
        504,
        'CLAIM_TIMEOUT',
        'Moving the app is taking longer than expected.',
        'Reload this page in a few minutes.'
      );
    }
    await new Promise((resolve) => setTimeout(resolve, CLAIM_POLL_MS));
  }
};
